/* Keep native Shopify filters/pagination, ordering the complete result set by availability. */
(() => {
  'use strict';
  if (window.HerbonataStockOrder) return;
  const indexCache = new Map();

  function plan(total, inStock, size, page) {
    const start = (page - 1) * size;
    const end = Math.min(start + size, total);
    const slices = [];
    for (const [availability, offset, count] of [['1', 0, inStock], ['0', inStock, total - inStock]]) {
      let from = Math.max(start, offset) - offset;
      const until = Math.min(end, offset + count) - offset;
      while (from < until) {
        const groupPage = Math.floor(from / size) + 1;
        const take = Math.min(until - from, size - from % size);
        slices.push({ availability, page: groupPage, from: from % size, take });
        from += take;
      }
    }
    return slices;
  }

  async function prepare(doc, sourceUrl) {
    const meta = doc.querySelector('[data-hb-stock-order]');
    const grid = doc.querySelector('#product-grid');
    if (!meta || !grid) return doc;
    const url = new URL(sourceUrl, window.location.origin);
    const chosen = [...new Set(url.searchParams.getAll('filter.v.availability'))];
    // Explicit availability filters remain fully native.
    if (chosen.length === 1 && ['0', '1'].includes(chosen[0])) return doc;
    const total = Number(meta.dataset.hbTotal);
    const stock = Number(meta.dataset.hbStock);
    const size = Number(meta.dataset.hbPageSize);
    const page = Math.max(1, Number(url.searchParams.get('page')) || 1);
    if (meta.dataset.hbStock === '' || !Number.isInteger(stock) || stock < 0 || stock > total || size < 1) return doc;
    if (stock === 0 || stock === total) return doc;

    const documents = new Map();
    const requestUrl = (availability, pageNumber, section = meta.dataset.hbSection) => {
      const request = new URL(url);
      request.searchParams.set('section_id', section);
      request.searchParams.set('filter.v.availability', availability);
      request.searchParams.set('page', String(pageNumber));
      return request;
    };
    const fetchHtml = async request => {
      const response = await fetch(request, { signal: AbortSignal.timeout(12000) });
      if (!response.ok) throw new Error(`Collection availability: HTTP ${response.status}`);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    };
    const gridPage = async (availability, pageNumber) => {
      const key = `${availability}/${pageNumber}`;
      if (!documents.has(key)) documents.set(key, fetchHtml(requestUrl(availability, pageNumber)));
      const html = await documents.get(key);
      return [...html.querySelectorAll('#product-grid > [data-product-id]')];
    };
    const stockSlices = plan(total, stock, size, page).filter(slice => slice.availability === '1');
    const cards = [];
    for (const slice of stockSlices) {
      const group = (await gridPage('1', slice.page)).slice(slice.from, slice.from + slice.take);
      if (group.length !== slice.take || group.some(card => card.dataset.hbAvailable !== '1')) throw new Error('Stock changed during pagination');
      cards.push(...group);
    }
    const outStart = Math.max(0, (page - 1) * size - stock);
    const outEnd = Math.min(total - stock, page * size - stock);
    if (outEnd > outStart) {
      // Shopify's variant availability filter also includes partially available
      // products. Index only genuinely unavailable products, then fetch their cards.
      const positions = [];
      let indexPage = 1;
      let offset = 0;
      while (positions.length < outEnd && indexPage) {
        const request = requestUrl('0', indexPage, 'herbonata-stock-index');
        const key = request.href;
        let entry = indexCache.get(key);
        if (!entry || Date.now() - entry.at > 30000) {
          if (indexCache.size > 40) indexCache.clear();
          entry = { at: Date.now(), promise: fetchHtml(request).then(html => {
            const node = html.querySelector('[data-hb-stock-index]');
            if (!node) throw new Error('Availability index absent');
            return JSON.parse(node.textContent);
          }) };
          indexCache.set(key, entry);
        }
        const data = await entry.promise;
        if (!Array.isArray(data.products) || offset > 25000) throw new Error('Availability index invalid');
        data.products.forEach((product, index) => {
          if (product.available === false) positions.push({ id: String(product.id), position: offset + index });
        });
        offset += data.products.length;
        if (data.next && data.next <= indexPage) throw new Error('Availability index did not advance');
        indexPage = data.next;
      }
      const wanted = positions.slice(outStart, outEnd);
      if (wanted.length !== outEnd - outStart) throw new Error('Stock count changed during pagination');
      const pages = [...new Set(wanted.map(item => Math.floor(item.position / size) + 1))];
      // At most three parallel HTML requests, even with many partial variants.
      let cursor = 0;
      await Promise.all(Array.from({length: Math.min(3, pages.length)}, async () => {
        while (cursor < pages.length) await gridPage('0', pages[cursor++]);
      }));
      for (const item of wanted) {
        const group = await gridPage('0', Math.floor(item.position / size) + 1);
        const card = group.find(node => node.dataset.productId === item.id);
        if (!card || card.dataset.hbAvailable !== '0') throw new Error('Availability changed during pagination');
        cards.push(card);
      }
    }
    if (cards.length !== Math.min(size, total - (page - 1) * size) || new Set(cards.map(card => card.dataset.productId)).size !== cards.length) {
      throw new Error('Missing or duplicate product in availability pages');
    }
    // Preserve optional promotional tiles and their positions.
    const promotions = [...grid.children].map((node, index) => ({ node, index })).filter(item => !item.node.hasAttribute('data-product-id'));
    grid.replaceChildren(...cards);
    for (const item of promotions) grid.insertBefore(item.node, grid.children[item.index] || null);
    grid.dataset.hbOrdered = 'true';
    return doc;
  }

  async function initialRender() {
    const grid = document.querySelector('#product-grid');
    if (!grid || !document.querySelector('[data-hb-stock-order]')) return;
    const href = window.location.href;
    const container = document.getElementById('ProductsGridContainer');
    container?.setAttribute('aria-busy', 'true');
    // Prepare off-DOM so a fast filter change cannot be overwritten by this request.
    const copy = new DOMParser().parseFromString(document.documentElement.outerHTML, 'text/html');
    try {
      const prepared = await prepare(copy, href);
      const ordered = prepared.querySelector('#product-grid[data-hb-ordered]');
      if (ordered && grid.isConnected && window.location.href === href) {
        grid.replaceChildren(...ordered.children);
        grid.dataset.hbOrdered = 'true';
        // LazyLoader is an ES-module export, not a window global.
        // Register the new cards after replacing the initial product grid.
        const { LazyLoader } = await import('@NextSkyTheme/lazy-load');
        new LazyLoader('.image-lazy-load');
        grid.closest('motion-items-effect')?.reloadAnimationEffect();
      }
    } catch (error) {
      // Native HTML remains usable if an optional network request fails.
      console.warn('Herbonata: classement par disponibilité temporairement indisponible.', error);
    } finally {
      container?.removeAttribute('aria-busy');
    }
  }

  window.HerbonataStockOrder = {
    plan,
    async prepare(doc, url) {
      try { return await prepare(doc, url); }
      catch (error) {
        console.warn('Herbonata: tri natif conservé après une erreur réseau ou un changement de stock.', error);
        return doc;
      }
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialRender, { once: true });
  else initialRender();
})();
